import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { ConversionSummaryComponent } from './conversion-summary/conversion-summary.component';
import { RecentConversionComponent } from './recent-conversion/recent-conversion.component';
import { XbrlFormComponent } from './xbrl-form/xbrl-form.component';
import { InteractiveToastComponent } from '../common/interactive-toast/interactive-toast.component';
import { ConversionStatusService } from '../../services/dashboard/conversion-status.service';
import { finalize } from 'rxjs';
import { ConversionEventService } from '../../services/common/conversion-event.service';
import { buildPageLoadFailureNotice } from '../../utils/page-load-failure.util';
import { PageLoadFailureToastGate } from '../../utils/page-load-failure-toast-gate.util';
import { DIAGNOSTIC_TOAST_LIFE_MS } from '../../utils/sheet-load-trace.util';
import { AuthFailureNoticeService } from '../../services/auth/auth-failure-notice.service';
import { RuntimeConfigService } from '../../services/common/runtime-config.service';

@Component({
  selector: 'dashboard',
  imports: [
    ConversionSummaryComponent,
    RecentConversionComponent,
    XbrlFormComponent,
    InteractiveToastComponent,
  ],
  providers: [MessageService],
  templateUrl: './dashboard.component.html',
})
export class DashboardComponent implements OnInit {
  conversionStatusService = inject(ConversionStatusService);
  conversionEvent = inject(ConversionEventService);
  authFailureNotice = inject(AuthFailureNoticeService);
  messageService = inject(MessageService);
  private destroyRef = inject(DestroyRef);
  isLoading = signal<boolean>(false);
  successCount = signal<number>(0);
  failedCount = signal<number>(0);
  reviewCount = signal<number>(0);
  // Caps this page's load-failure toast to one per class — see
  // PageLoadFailureToastGate's own docblock. Applied uniformly across every
  // page-load-failure.util.ts consumer, not only the pages with more than
  // one load-up API call, so the same class of bug can't resurface quietly
  // the moment a second call is added here.
  // REFACTORED-DIAGNOSTIC-CODES-FLAG: page-level toasts are silent in production.
  private readonly runtimeConfig = inject(RuntimeConfigService);
  private readonly loadFailureToastGate = new PageLoadFailureToastGate(undefined, () =>
    this.runtimeConfig.refactoredDiagnosticCodesEnabled(),
  );

  fetchConversionStatusCount() {
    this.isLoading.set(true);
    this.conversionStatusService
      .fetchConversionStatusCount()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (data) => {
          if (data) {
            this.successCount.set(data.successCount ?? 0);
            // this.failedCount.set(data.failedCount);
            this.reviewCount.set(data.reviewCount ?? 0); // pending data.reviewCount
          }
        },
        error: (error: unknown) => {
          const notice = buildPageLoadFailureNotice('DASHBOARD_SUMMARY', error);
          if (notice.silent) {
            // AUTH: this page is about to be navigated away from by the
            // forced-logout redirect — stage the composed summary for the login page.
            this.authFailureNotice.stage(notice.summary);
          } else if (this.loadFailureToastGate.shouldToast(notice.failure)) {
            this.messageService.add({
              key: 'dashboard',
              severity: 'error',
              summary: notice.summary,
              life: DIAGNOSTIC_TOAST_LIFE_MS,
            });
          }
        },
      });
  }

  ngOnInit(): void {
    this.conversionEvent.refetch$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.fetchConversionStatusCount());
    this.fetchConversionStatusCount();
  }
}
